import { gql, useQuery } from "@apollo/client";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import React from "react";
import Error from "../components/Error/Error";
import Loading from "../components/Loading/Loading";
import ProcedureSectionsGrid from "../components/ProcedureSectionsGrid/ProcedureSectionsGrid";
import { appName, getPublicRuntimeConfig, isCI } from "../config";
import HomeLayoutContainer from "../containers/Layouts/HomeLayoutContainer";
import ProcedureListingFragment from "../containers/ProcedureListingFragment";
import { getIndustrySlug, getSearchQuery, getTopicSlug } from "../core/getParam";
import logger from "../core/logger";
import { getHomePageRoute } from "../core/routes";
import { initializeApollo } from "../graphql/client";
import { IIndexPageQuery, IIndexPageQueryVariables } from "../graphql/gpl-graphQLTypes.gen";

const indexQuery = gql`
  query IndexPage {
    proceduresSummary(limit: 50) {
      slug
      label
      totalCount
      type
      data {
        ...ProcedureListingFragment
      }
    }
  }
  ${ProcedureListingFragment}
`;

export const getServerSideProps: GetServerSideProps = async () => {
  const apolloClient = initializeApollo();
  if (!isCI) {
    await Promise.all([
      HomeLayoutContainer.runDataQueries(apolloClient),
      apolloClient.query<IIndexPageQuery, IIndexPageQueryVariables>({ query: indexQuery }),
    ]);
  }
  return {
    props: { initialApolloState: apolloClient.cache.extract() },
  };
};

export default function IndexPage() {
  const router = useRouter();
  React.useEffect(() => {
    const homeRouteFromParams = getHomePageRoute({
      industry: getIndustrySlug(router),
      topic: getTopicSlug(router),
      searchQuery: getSearchQuery(router),
    });

    if (homeRouteFromParams !== "/") {
      // If we have some params for some reason (manually typed?) redirect
      router.replace(homeRouteFromParams).catch((err) => {
        logger.error(err);
      });
    }
  }, [router]);

  const { data, loading, error } = useQuery<IIndexPageQuery, IIndexPageQueryVariables>(indexQuery, {
    skip: isCI,
  });
  const sections = data?.proceduresSummary ?? [];
  if (isCI) {
    return (
      <div>
        <div style={{ display: "none" }}>__build_is_not_ready__</div>
        <Loading />
      </div>
    );
  }

  function renderContent() {
    if (data) {
      return <ProcedureSectionsGrid isHomePage={true} sections={sections} title="All Industries" />;
    }
    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <Error error={error} />;
    }
    return null;
  }

  return (
    <HomeLayoutContainer
      metaDescription="Procedures to improve your maintenance, safety, and operations."
      metaTitle={`${appName} | MaintainX`}
      metaUrl={`${getPublicRuntimeConfig().frontendUrl}`}
    >
      {renderContent()}
    </HomeLayoutContainer>
  );
}
